// Аналитика воронки Mini App → прод-эндпоинт /v1/track.
// По образцу site/track.js: fetch keepalive, credentials 'omit', все ошибки глотаем.
// Идентификатор: в Telegram — tg user id (`tg_<id>`, уникальные ЛЮДИ; решение Iefym
// 2026-07-12), в браузере — анонимный device-id `mini_<rand>` из localStorage.
//
// Имена событий — строго из бэкенд-вайтлиста ALLOWED_EVENTS
// (backend/src/crm/public.ts): app_open, nick_entered, review_opened, cta_click,
// training_action — все разрешены, маппинг имён не потребовался. Тренировка шлёт
// training_action с props.label='train_blunders' (label — разрешённый ключ).
//
// Ключи props — из ALLOWED_PROP_KEYS того же файла; неизвестные ключи бэкенд молча
// отбрасывает (анти-PII), поэтому прикладные значения кладём в разрешённые ключи:
//   nick_entered   site   → props.platform  ('lichess'|'chesscom'|'auto')
//   review_opened  site   → props.platform, engine → props.label ('seeded'|'wasm')
//   cta_click      target → props.href ('extension'), placement → props.label
//   (source:'miniapp' — во всех событиях, ключ 'source' в вайтлисте есть.)
//
// В dev (import.meta.env.DEV) ничего не шлём — логируем в console.debug.

import { getTg } from '../tg';

const API_BASE = 'https://api.getchesscoach.com';
const ANON_KEY = 'cc_mini_anon';

type EventName = 'app_open' | 'nick_entered' | 'review_opened' | 'cta_click' | 'training_action';
type PropVal = string | number | boolean;

interface TrackPayload {
  name: EventName;
  props: Record<string, PropVal>;
  anon_id: string;
  ts: number;
}

/**
 * Идентификатор юзера для аналитики. Решение Iefym 2026-07-12: в Telegram
 * считаем УНИКАЛЬНЫХ людей по tg user id (`tg_<id>` — стабилен между
 * устройствами/переустановками); вне Telegram — прежний device-id `mini_<rand>`.
 */
export function anonId(): string {
  const tgId = getTg()?.initDataUnsafe?.user?.id;
  if (tgId != null) return `tg_${tgId}`;
  let id: string | null = null;
  try {
    id = localStorage.getItem(ANON_KEY);
  } catch {
    id = null;
  }
  if (!id || !id.startsWith('mini_')) {
    const rand =
      globalThis.crypto?.randomUUID?.() ??
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    id = `mini_${rand}`;
    try {
      localStorage.setItem(ANON_KEY, id);
    } catch {
      /* private mode — держим id только в этой сессии */
    }
  }
  return id;
}

function send(name: EventName, props: Record<string, PropVal>): void {
  const payload: TrackPayload = {
    name,
    props: { source: 'miniapp', ...props },
    anon_id: anonId(),
    ts: Date.now(),
  };
  // import.meta.env.DEV инлайнится Vite (в прод-сборке = false) → dev-ветка целиком
  // вырезается из бандла, боевой JS её не содержит.
  if (import.meta.env.DEV) {
    console.debug('[track:dev]', payload);
    return;
  }
  try {
    fetch(`${API_BASE}/v1/track`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      /* аналитика best-effort — не роняем UI */
    });
  } catch {
    /* fetch может кинуть синхронно в экзотических webview — глотаем */
  }
}

/** Запуск приложения. `shared` — открытие по диплинку на чужую партию (виральность). */
export function trackAppOpen(shared = false): void {
  send('app_open', shared ? { label: 'shared' } : {});
}

/** Ник введён и принят. `site` — выбранный источник (lichess/chesscom/auto). */
export function trackNickEntered(site: string): void {
  send('nick_entered', { platform: site });
}

/** Открыт разбор партии. `engine` — путь оценки (мгновенный [%eval] или WASM). */
export function trackReviewOpened(site: string, engine: 'seeded' | 'wasm'): void {
  send('review_opened', { platform: site, label: engine });
}

/** Клик по CTA установки расширения. `placement` — где именно (games_footer/review_card). */
export function trackCtaClick(placement: string): void {
  send('cta_click', { href: 'extension', label: placement });
}

/** Сессия тренажёра запущена. label — режим («зевки»/«дебюты»/«игра с движком»).
 *  Значения кладём в разрешённый ключ props.label (ALLOWED_PROP_KEYS), само событие
 *  training_action входит в ALLOWED_EVENTS. */
export function trackTraining(label: 'train_blunders' | 'train_openings' | 'play_engine' = 'train_blunders'): void {
  send('training_action', { label });
}
